import { useEffect, useState } from "react";
import { CopyIcon, DeleteIcon, EditIcon, TableDatePicker } from "themes/Icon";
import { isPermit } from "utils/permissionCheck";
import ButtonComp from "../Button";
import CheckboxComp from "../Checkbox";
import DatePickerComp from "../DatePicker";
import EmptyState from "../EmptyState";
import PaginationComp from "../Pagination";
import SearchComp from "../Search";
import SelectComp from "../Select";
import TagComp from "../Tag";
import TooltipComp from "../Tooltip";
import "./table_comp.scss";

/** Maps column `dropDown` entries to Select options (supports `{ name, value }` or plain strings). */
function mapSelectCategoryDropDown(dropDown) {
  if (!Array.isArray(dropDown)) return [];
  return dropDown.map((item) => {
    if (item == null) return { label: "", value: "" };
    if (typeof item === "string") {
      const lower = item.toLowerCase();
      const value = lower === "alert" ? 2 : lower === "general" ? 1 : item;
      return { label: item, value };
    }
    return {
      label: item.name ?? item.label ?? "",
      value: item.value ?? item.id ?? item.name,
    };
  });
}

function TableComp({
  column,
  data,
  dataClick,
  copyAction = true,
  onDeleteAction,
  currentPageNo,
  setCurrentPageNo = () => {},
  totalPageNo,
  handleEdit,
  handleDuplicate = () => {},
  selectedData,
  setSelectedData = () => {},
  isLoading,
  tabState,
  filterTitle,
  filterCode,
  filterType,
  setStatus = () => {},
  setFilterTitle = () => {},
  setFilterAlertDate = () => {},
  setFilterExpiryDate = () => {},
  setFilterCode = () => {},
  setFilterType = () => {},
  tabActive,
  editAction = false,
  showEditOnlyWhenResubmission = false,
  hideDeleteAction = false,
  enableRowSelectionAndDelete = true,
}) {
  const [currentPageData, setCurrentPageData] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [alertDate, setAlertDate] = useState(null);
  const [expiryDate, setExpiryDate] = useState(null);
  const [parentSelected, setParentSelected] = useState(false);
  const [indeterminate, setIndeterminate] = useState(false);

  useEffect(() => {
    setCurrentPageData(data);
    setParentSelected(false);
    setIndeterminate(false);
    setSelectedData([]);
  }, [data]);

  const handlePageChange = (page) => {
    setCurrentPageNo(page);
  };

  const handleParentCheckboxChange = (isChecked) => {
    setIndeterminate(false);
    setParentSelected(isChecked);
    if (isChecked) {
      const allRowsData = currentPageData.map((item) => ({ ...item }));
      setSelectedData(allRowsData);
    } else {
      setSelectedData([]);
    }
  };

  const handleChildCheckboxChange = (row, isChecked) => {
    let updatedSelectedData;
    if (isChecked) {
      updatedSelectedData = [...selectedData, { ...row }];
    } else {
      updatedSelectedData = selectedData.filter((item) => item.id !== row?.id);
    }
    setSelectedData(updatedSelectedData);
    if (updatedSelectedData.length === currentPageData.length) {
      setParentSelected(true);
      setIndeterminate(false);
    } else if (updatedSelectedData.length > 0) {
      setParentSelected(false);
      setIndeterminate(true);
    } else {
      setParentSelected(false);
      setIndeterminate(false);
    }
  };

  const isChildChecked = (id) => selectedData?.some((item) => item.id === id);

  const formatDate = (dateString) => {
    const [day, month, year] = dateString.split("-");
    return `${year}-${month}-${day}`;
  };

  const handleAlertDateChange = (dateString) => {
    setCurrentPageNo(1);
    if (dateString) {
      const formattedDate = formatDate(dateString);
      setFilterAlertDate(formattedDate);
    } else {
      setFilterAlertDate(null);
    }
  };

  const handleExpiryDateChange = (dateString) => {
    setCurrentPageNo(1);
    if (dateString) {
      const formattedDate = formatDate(dateString);
      setFilterExpiryDate(formattedDate);
    } else {
      setFilterExpiryDate(null);
    }
  };

  const handleStatusChange = (value) => {
    setCurrentPageNo(1);

    if (value === "Expired") {
      setStatus(6);
      setSelectedStatus(value);
    } else if (value === "Published") {
      setStatus(4);
      setSelectedStatus("Published");
    } else {
      //for showing all status pass 4 both published and expired
      setStatus(4);
    }
  };

  const handleSearchChange = (event, type) => {
    setCurrentPageNo(1);
    const value = event.target.value;
    if (type === "title") {
      setFilterTitle(value);
    } else {
      setFilterCode(value);
    }
  };

  const handleCategoryChange = (value) => {
    setCurrentPageNo(1);
    if (value == null || value === "") {
      setSelectedCategory(null);
      setFilterType(null);
      return;
    }

    setSelectedCategory(value);
    const n = Number(value);
    if (n === 2) {
      setFilterType(2);
    } else if (n === 1) {
      setFilterType(1);
    } else if (typeof value === "string") {
      const lower = value.toLowerCase();
      if (lower === "alert") setFilterType(2);
      else if (lower === "general") setFilterType(1);
      else setFilterType(1);
    } else {
      setFilterType(1);
    }
  };

  const SkeletonRow = ({ columns }) => (
    <tr className="table-row">
      {columns.map((col, index) => (
        <td key={index} className="table-data" style={{ width: col.width }}>
          <div className="skeleton-box"></div>
        </td>
      ))}
    </tr>
  );

  const clearFilters = () => {
    setSelectedCategory(null);
    setAlertDate(null);
    setExpiryDate(null);
    setCurrentPageData(data);
    setFilterTitle("");
    setFilterType(null);
    setFilterAlertDate(null);
    setFilterCode(null);
    setFilterExpiryDate(null);
    setCurrentPageNo(1);
    setSelectedStatus(null);

    if (tabActive === "1") {
      //for showing all status pass 4 both published and  expired

      setStatus(4);
    }
  };

  const onDataClick = (row) => {
    dataClick(row);
  };

  useEffect(() => {
    clearFilters();
    setParentSelected(false);
    setIndeterminate(false);
  }, [tabActive]);

  return (
    <div className="table-wrapper">
      <table className="table-comp">
        <thead className="table-head">
          <tr className="table-row">
            {column.map((col, i) => (
              <th
                className="table-header"
                key={i}
                style={{ minWidth: col.width }}
              >
                <span className="flex align-center gap-3">
                  {i === 0 &&
                    isPermit(4, "delete") &&
                    enableRowSelectionAndDelete && (
                    <CheckboxComp
                      isChecked={parentSelected}
                      indeterminate={indeterminate}
                      onChange={(checked) =>
                        handleParentCheckboxChange(checked)
                      }
                    />
                  )}
                  {col.title}
                </span>
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="table-body">
          <tr className="table-row">
            {column.map((col, i) => (
              <td className="table-data" style={{ width: col.width }} key={i}>
                {col.filter === "searchTitle" ? (
                  <SearchComp
                    className="table-search"
                    height={33}
                    bgNone
                    placeholder="Search titles..."
                    value={filterTitle}
                    onChange={(e) => handleSearchChange(e, "title")}
                  />
                ) : col.filter === "searchRecipients" ? (
                  <SearchComp
                    className="table-search"
                    height={33}
                    bgNone
                    placeholder="Search recipients..."
                    value={filterCode}
                    onChange={(e) => handleSearchChange(e, "recipients")}
                  />
                ) : col.filter === "selectCategory" ? (
                  <SelectComp
                    className="table-select"
                    value={selectedCategory ?? undefined}
                    placeholder={col.title}
                    onDeleteAction
                    onChange={handleCategoryChange}
                    data={mapSelectCategoryDropDown(col.dropDown)}
                    onClear={() => {
                      setSelectedCategory(null);
                      setFilterType(null);
                    }}
                  />
                ) : col.filter === "selectStatusPublished" ? (
                  <SelectComp
                    value={selectedStatus ?? undefined}
                    className="table-select"
                    placeholder={col.title}
                    onChange={handleStatusChange}
                    data={col.dropDown.map((item) => ({
                      label: item,
                      value: item,
                    }))}
                    onClear={() => {
                      setSelectedStatus(null);
                      setStatus(4);
                    }}
                  />
                ) : col.filter === "datePickerAlert" ? (
                  <DatePickerComp
                    className="table-date-picker"
                    suffixIcon={<TableDatePicker />}
                    onChange={handleAlertDateChange}
                    setValue={setAlertDate}
                    value={alertDate}
                  />
                ) : col.filter === "datePickerExpiry" ? (
                  <DatePickerComp
                    className="table-date-picker"
                    suffixIcon={<TableDatePicker />}
                    onChange={handleExpiryDateChange}
                    setValue={setExpiryDate}
                    value={expiryDate}
                  />
                ) : null}
              </td>
            ))}
          </tr>
          {(filterTitle ||
            selectedCategory ||
            selectedStatus ||
            filterCode ||
            alertDate ||
            expiryDate) && (
            <div className="clr-btn-container">
              <ButtonComp height={16} variant="link" onClick={clearFilters}>
                Clear filters
              </ButtonComp>
            </div>
          )}
          {!isLoading ? (
            currentPageData?.length > 0 ? (
              currentPageData.map((row) => (
                <tr className="table-row" key={row?.id}>
                  {column.map((col, colIndex) => (
                    <td
                      style={{ width: col.width }}
                      className="table-data"
                      key={colIndex}
                    >
                      {colIndex === 0 ? (
                        <span className="flex align-center gap-3">
                          {isPermit(4, "delete") &&
                            enableRowSelectionAndDelete && (
                            <CheckboxComp
                              isChecked={isChildChecked(row?.id)}
                              onChange={(checked) =>
                                handleChildCheckboxChange(row, checked)
                              }
                            />
                          )}
                          <TooltipComp
                            title={
                              row[col.title?.toLowerCase().replace(" ", "")]
                            }
                          >
                            <span
                              className="table-title"
                              onClick={() => {
                                onDataClick(row);
                              }}
                            >
                              {row[col.title?.toLowerCase().replace(" ", "")]}
                            </span>
                          </TooltipComp>
                        </span>
                      ) : col.chip ? (
                        <div>
                          <TagComp
                            color={
                              row[col.title?.toLowerCase()]?.toLowerCase() ===
                              "published"
                                ? ""
                                : row[
                                    col.title?.toLowerCase()
                                  ]?.toLowerCase() === "expired"
                                ? "pink"
                                : row[
                                    col.title?.toLowerCase()
                                  ]?.toLowerCase() === "sent" ||
                                  row[
                                    col.title?.toLowerCase()
                                  ]?.toLowerCase() === "active"
                                ? "green"
                                : row[
                                    col.title?.toLowerCase()
                                  ].toLowerCase() === "rejected" ||
                                  row[
                                    col.title?.toLowerCase()
                                  ].toLowerCase() === "inactive"
                                ? "red"
                                : row[
                                    col.title?.toLowerCase()
                                  ].toLowerCase() === "business"
                                ? "blue"
                                : ""
                            }
                          >
                            {row[col.title?.toLowerCase()]}
                          </TagComp>
                        </div>
                      ) : col.filter === "datePickerAlert" ||
                        col.filter === "datePickerExpiry" ? (
                        col.filter === "datePickerAlert" ? (
                          row["alert_date_time"]
                        ) : (
                          row[col.title?.toLowerCase().replace(" ", "")] ||
                          "N/A"
                        )
                      ) : (
                        <span className="flex align-center gap-2">
                          {row[col.title?.toLowerCase().replace(" ", "")]}
                        </span>
                      )}
                      <div className="action-container">
                        {editAction && (
                          showEditOnlyWhenResubmission
                            ? row?.allow_resubmission_flg && (
                                <ButtonComp
                                  onClick={() => handleEdit(row)}
                                  height={32}
                                  rounded
                                >
                                  <EditIcon />
                                </ButtonComp>
                              )
                            : (
                                <ButtonComp
                                  onClick={() => handleEdit(row)}
                                  height={32}
                                  rounded
                                >
                                  <EditIcon />
                                </ButtonComp>
                              )
                        )}

                        {!hideDeleteAction && isPermit(4, "delete") && (
                          <ButtonComp
                            onClick={() => {
                              onDeleteAction(row);
                            }}
                            height={32}
                            rounded
                          >
                            <DeleteIcon />
                          </ButtonComp>
                        )}
                        {copyAction && isPermit(4, "create") && (
                          <ButtonComp
                            onClick={() => {
                              handleDuplicate(row);
                            }}
                            height={32}
                            rounded
                          >
                            <CopyIcon />
                          </ButtonComp>
                        )}
                      </div>
                    </td>
                  ))}
                </tr>
              ))
            ) : (
              <EmptyState />
            )
          ) : (
            Array(10)
              .fill(0)
              .map((_, index) => <SkeletonRow key={index} columns={column} />)
          )}
        </tbody>
      </table>
      <PaginationComp
        currentPageNo={currentPageNo}
        total={totalPageNo}
        onChange={handlePageChange}
      />
    </div>
  );
}

export default TableComp;
